// frontend/src/pages/admin/QuotasPage.tsx
import { useCallback, useEffect, useState } from "react";
import {
  api,
  type AdminEffectiveQuotas,
  type AdminQuotasView,
  type AdminUserView,
} from "../../lib/api";

const FIELDS: Array<{
  key: keyof AdminEffectiveQuotas;
  label: string;
  hint: string;
  formatDefault: (n: number) => string;
}> = [
  {
    key: "max_user_storage_bytes",
    label: "Max user storage (bytes)",
    hint: "Total bytes across all this user's active artifacts.",
    formatDefault: (n) => `${(n / (1024 * 1024)).toFixed(0)} MB`,
  },
  {
    key: "anthropic_rate_limit_per_hour",
    label: "Anthropic requests per hour",
    hint: "Rolling 1-hour window over /api/anthropic/messages.",
    formatDefault: (n) => `${n} req/h`,
  },
  {
    key: "anthropic_token_limit_per_day",
    label: "Anthropic tokens per day",
    hint: "Rolling 24h window — input + output tokens combined.",
    formatDefault: (n) => `${n.toLocaleString()} tok/day`,
  },
];

/** Admin → Quotas sub-page. Pick a user, edit per-user override values. */
export function QuotasPage() {
  const [users, setUsers] = useState<AdminUserView[] | null>(null);
  const [selected, setSelected] = useState<string>("");
  const [view, setView] = useState<AdminQuotasView | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<"" | "save">("");

  useEffect(() => {
    api.admin.listUsers().then((list) => {
      setUsers(list);
      if (list.length > 0 && !selected) setSelected(list[0].email);
    }).catch((e: Error) => setError(e.message));
  }, [selected]);

  const loadView = useCallback((email: string) => {
    setError(null);
    api.admin.quotas
      .get(email)
      .then((v) => {
        setView(v);
        setDraft({});  // reset draft on user change
      })
      .catch((e: Error) => setError(e.message));
  }, []);

  useEffect(() => {
    if (selected) loadView(selected);
  }, [selected, loadView]);

  async function save() {
    if (!view) return;
    setBusy("save");
    setError(null);
    try {
      const patch: Record<string, number | null | string[]> = {};
      const clear: string[] = [];
      for (const f of FIELDS) {
        const raw = draft[f.key];
        if (raw === undefined) continue;
        if (raw === "") {
          clear.push(f.key);
        } else {
          const n = Number(raw);
          if (Number.isFinite(n) && n > 0) {
            patch[f.key] = Math.floor(n);
          }
        }
      }
      if (clear.length > 0) patch.clear = clear;
      const updated = await api.admin.quotas.patch(view.email, patch);
      setView(updated);
      setDraft({});
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy("");
    }
  }

  if (users === null) return <p className="text-slate-500">Loading…</p>;

  return (
    <div>
      <h1 className="mb-2 text-2xl font-semibold">Quotas</h1>
      <p className="mb-4 text-sm text-slate-500">
        Per-user overrides beat the env-var defaults. Leave a field blank to
        keep the current value; clear it (delete the input) to fall back to
        the default.
      </p>

      <section className="mb-4 flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-3">
        <label className="text-sm font-semibold text-slate-700">User</label>
        <select
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          className="flex-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm"
        >
          {users.map((u) => (
            <option key={u.email} value={u.email}>
              {u.email} {u.role === "admin" ? "(admin)" : ""}
            </option>
          ))}
        </select>
      </section>

      {error && (
        <p className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {error}
        </p>
      )}

      {view ? (
        <div className="space-y-4">
          {FIELDS.map((f) => {
            const overrideValue = view.overrides[f.key];
            const effectiveValue = view.effective[f.key];
            const draftValue = draft[f.key] ?? (overrideValue !== undefined ? String(overrideValue) : "");
            const isOverride = overrideValue !== undefined;
            return (
              <div key={f.key} className="rounded-lg border border-slate-200 bg-white p-4">
                <div className="flex items-baseline justify-between">
                  <h3 className="text-sm font-semibold">{f.label}</h3>
                  <span className="text-xs text-slate-500">
                    Effective: <span className="font-medium text-slate-800">{f.formatDefault(effectiveValue)}</span>
                    {isOverride ? " (override)" : " (default)"}
                  </span>
                </div>
                <p className="mt-1 text-xs text-slate-500">{f.hint}</p>
                <div className="mt-2 flex items-center gap-2">
                  <input
                    type="number"
                    value={draftValue}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, [f.key]: e.target.value }))
                    }
                    placeholder="Use default"
                    className="w-64 rounded-md border border-slate-300 px-3 py-1.5 text-sm"
                  />
                  {isOverride && (
                    <button
                      type="button"
                      onClick={() =>
                        setDraft((d) => ({ ...d, [f.key]: "" }))
                      }
                      className="rounded border border-slate-200 px-2 py-1 text-xs hover:bg-slate-50"
                    >
                      Clear override
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          <div className="flex justify-end">
            <button
              type="button"
              onClick={save}
              disabled={busy === "save" || Object.keys(draft).length === 0}
              className="rounded-md bg-slate-900 px-4 py-1.5 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
            >
              {busy === "save" ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      ) : (
        <p className="text-slate-500">Pick a user to view their quotas.</p>
      )}
    </div>
  );
}
